import { inject, Renderer2, RendererFactory2, Service } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { strings as deStrings } from 'ngx-timeago/language-strings/de';
import { strings as enStrings } from 'ngx-timeago/language-strings/en';
import { strings as esStrings } from 'ngx-timeago/language-strings/es';
import { strings as frStrings } from 'ngx-timeago/language-strings/fr';
import { strings as itStrings } from 'ngx-timeago/language-strings/it';
import { firstValueFrom } from 'rxjs';

import { DEFAULT_LANGUAGE } from '@constants';
import { SystemSettings } from '@models';
import { extractLanguageCode } from '@shared/utils/locale';
import { AppState, setLanguages, setLocale } from '@store';
import dayjs from 'dayjs/esm';
import { TimeagoIntl } from 'ngx-timeago';
import { UtilitiesService } from './utilities.service';

const stringsMap: Record<string, typeof enStrings> = {
  en: enStrings,
  de: deStrings,
  fr: frStrings,
  it: itStrings,
  es: esStrings,
};

@Service()
export class i18nService {
  translateService = inject(TranslateService);
  store = inject<Store<AppState>>(Store);
  utilitiesService = inject(UtilitiesService);
  timeagoIntl = inject(TimeagoIntl);
  rendererFactory = inject(RendererFactory2);

  renderer: Renderer2;
  appliedLocale?: string;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  setLocale = async (locale: string, doNotSetUserLocale?: boolean) => {
    const normalizedLocale = extractLanguageCode(locale);

    this.appliedLocale = locale;

    this.timeagoIntl.strings = stringsMap[normalizedLocale] || enStrings;

    this.timeagoIntl.changes.next();

    this.store.dispatch(
      setLocale({
        payload: normalizedLocale,
      })
    );

    this.setCurrentLocale(locale);
    dayjs.locale(locale);

    const rootElement = this.renderer.selectRootElement('html', true);

    this.renderer.setAttribute(rootElement, 'lang', normalizedLocale);

    this.translateService.use(normalizedLocale).subscribe(() => {
      this.renderer.setAttribute(
        rootElement,
        'dir',
        this.translateService.instant('WEB_I18N_DIR')
      );
    });

    if (!doNotSetUserLocale) {
      this.setUserLocale(locale);
    }
  };

  getLocale = (systemSettings?: SystemSettings) => {
    let locale = this.getCurrentLocale();

    if (systemSettings?.LANGUAGES && systemSettings?.DEFAULT_LANGUAGE) {
      const languages = systemSettings.LANGUAGES.filter(
        (lang) => !lang.startsWith('ar')
      );

      if (!locale) {
        const windowLocale = window.navigator.language;

        const match = languages.find(
          (lang) =>
            extractLanguageCode(windowLocale) === extractLanguageCode(lang)
        );

        this.setCurrentLocale(
          match
            ? windowLocale.replace('-', '_')
            : systemSettings.DEFAULT_LANGUAGE
        );

        locale = this.getCurrentLocale();
      }

      this.setLanguages(languages);
    }

    locale ||= DEFAULT_LANGUAGE;

    if (locale !== this.appliedLocale) {
      this.setLocale(locale, true);
    }

    return locale;
  };

  setCurrentLocale = (locale: string) => {
    localStorage.setItem('locale', locale);
  };

  getCurrentLocale = () =>
    extractLanguageCode(
      localStorage.getItem('locale')?.replace(/"/g, '') || ''
    );

  setUserLocale = (locale: string) => {
    localStorage.setItem('user_locale', locale);
  };

  getUserLocale = () => {
    const raw = localStorage.getItem('user_locale');

    return raw?.includes('"') ? raw.replace(/"/g, '') : raw;
  };

  getCountries = async () => {
    const locale = this.getCurrentLocale();

    try {
      const response = await firstValueFrom(
        this.utilitiesService.getSystemCountries(locale)
      );

      return response;
    } catch {
      return undefined;
    }
  };

  setLanguages = (languages: string[]) => {
    this.store.dispatch(setLanguages({ payload: languages }));
  };
}
